import { createImageData } from "canvas";
import {
	notchRadius,
	notchRidgeWidth,
	realWorldBrickSize,
} from "../model/index.ts";
import type { LabComponents } from "../utils/colour-conversions.ts";
import { labToRgb, rgbToLab } from "../utils/colour-conversions.ts";
import { imageDataToPixelChannelRows } from "../utils/image-data.ts";

// const highlightSizeRatio = 0.45;
const notchShadowSizeRatio = 0.8;
const notchShadowPositionRatio = { x: 0.455, y: 0.55 };
// const notchHighlightPositionRatio = { x: 0.71, y: 0.29 };
const borderRatio = 0.037;

type CanvasContext = CanvasFillStrokeStyles &
	CanvasPathDrawingStyles &
	CanvasDrawPath &
	CanvasRect &
	CanvasPath &
	CanvasShadowStyles &
	CanvasState &
	CanvasTransform &
	CanvasImageData;

// Offsets were created for hsp which is in the range 255, but lab in range 100
const offsetModifier = 100 / 255;
function modifyRgbL({ l, a, b }: LabComponents, offset: number) {
	const notchRidgeLab = {
		a,
		b,
		l: Math.min(100, Math.max(0, l + offset * offsetModifier)),
	};
	const rgb = labToRgb(notchRidgeLab);
	return `#${rgb.r.toString(16).padStart(2, "0")}${rgb.g.toString(16).padStart(2, "0")}${rgb.b.toString(16).padStart(2, "0")}`;
}

function draw45DegreesLinearGradientCircle(
	ctx: CanvasContext,
	x: number,
	y: number,
	radius: number,
	startColour: string,
	midColour: string,
	endColour: string,
) {
	const gradientOffset = radius * Math.cos(Math.PI / 4);
	const start = {
		x: x - gradientOffset,
		y: y + gradientOffset,
	};
	const end = {
		x: x + gradientOffset,
		y: y - gradientOffset,
	};
	const gradient = ctx.createLinearGradient(start.x, start.y, end.x, end.y);
	gradient.addColorStop(0, startColour);
	gradient.addColorStop(0.85, midColour);
	gradient.addColorStop(1, endColour);
	ctx.fillStyle = gradient;
	ctx.beginPath();
	ctx.arc(x, y, radius, 0, 2 * Math.PI, false);
	ctx.fill();
}

type DrawOutlineConfig = {
	readonly top: boolean;
	readonly bottom: boolean;
	readonly left: boolean;
	readonly right: boolean;
};

// Note: Get lots of complaints in console about willReadFrequently not being set.
// Not sure if it's relevant though since we're changing the data before each read.
// i.e. each read is unique.

// TODO: These colour computations are pretty expensive. Should pass in a
// BrickRenderColours package instead that create once per session.
// Did some measurement and the time taken is almost imperceptible for a
// single brick. Maybe show a little for up to 50 bricks (max per render).
// But overall not a high priority
function baseRenderBrickUnit(
	ctx: CanvasContext,
	colourString: string,
	brickSize: number,
	drawOutline: DrawOutlineConfig,
) {
	const baseNum = parseInt(colourString.slice(1), 16);
	const baseR = (baseNum >> 24) & 0xff;
	const baseG = (baseNum >> 16) & 0xff;
	const baseB = (baseNum >> 8) & 0xff;
	const baseLab = rgbToLab(baseR, baseG, baseB);

	const notchRidgeColour = modifyRgbL(baseLab, -9);
	const notchMidRgbColour = modifyRgbL(baseLab, 16);
	const notchEndRgbColour = modifyRgbL(baseLab, 60);
	const notchRidgeMidColour = modifyRgbL(baseLab, 55);
	const notchRidgeEndColour = modifyRgbL(baseLab, 105);

	const halfBrickSize = brickSize * 0.5;
	const halfNotchSize =
		(halfBrickSize * notchRadius.mm) / realWorldBrickSize.mm;
	const drawNotchRidgeWidth =
		(notchRidgeWidth.mm * brickSize) / realWorldBrickSize.mm;

	// Base/underneath notch
	// TODO: Draw outline off if drawing a baseplate
	ctx.fillStyle = colourString;
	ctx.fillRect(0, 0, brickSize, brickSize);

	// Outline
	const borderWidth = brickSize * borderRatio;

	// Don't do this check. Having a small border is still valuable.
	// && borderWidth >= 1
	if (
		drawOutline.top ||
		drawOutline.bottom ||
		drawOutline.left ||
		drawOutline.right
	) {
		const darkGradientEnd = modifyRgbL(baseLab, -29);
		const lightGradientEnd = modifyRgbL(baseLab, 72);

		// Top
		if (drawOutline.top) {
			const topGradient = ctx.createLinearGradient(0, borderWidth, 0, 0);
			topGradient.addColorStop(0, colourString);
			topGradient.addColorStop(1, lightGradientEnd);
			ctx.fillStyle = topGradient;
			ctx.fillRect(0, 0, brickSize, Math.min(1, borderWidth));
			// ctx.strokeStyle = topGradient;
			// ctx.lineWidth = borderWidth;
			// ctx.beginPath();
			// ctx.moveTo(0, borderWidth / 2);
			// ctx.lineTo(brickSize, borderWidth / 2);
			// ctx.stroke();
		}

		// Bottom
		if (drawOutline.bottom) {
			const bottomGradient = ctx.createLinearGradient(
				0,
				brickSize - borderWidth,
				0,
				brickSize,
			);
			bottomGradient.addColorStop(0, colourString);
			bottomGradient.addColorStop(1, darkGradientEnd);
			ctx.fillStyle = bottomGradient;
			ctx.fillRect(0, brickSize - borderWidth, brickSize, borderWidth);
		}

		// Left
		if (drawOutline.left) {
			const leftGradient = ctx.createLinearGradient(0, 0, borderWidth, 0);
			leftGradient.addColorStop(0, darkGradientEnd);
			leftGradient.addColorStop(1, colourString);
			ctx.fillStyle = leftGradient;
			ctx.beginPath();
			ctx.moveTo(0, 0);
			ctx.lineTo(borderWidth, borderWidth);
			ctx.lineTo(borderWidth, brickSize - borderWidth);
			ctx.lineTo(0, brickSize);
			ctx.lineTo(0, 0);
			ctx.fill();
		}

		// Right
		if (drawOutline.right) {
			const rightGradient = ctx.createLinearGradient(
				brickSize - borderWidth,
				0,
				brickSize,
				0,
			);
			rightGradient.addColorStop(0, colourString);
			rightGradient.addColorStop(1, lightGradientEnd);
			ctx.fillStyle = rightGradient;
			ctx.beginPath();
			ctx.moveTo(brickSize, 0);
			ctx.lineTo(brickSize - borderWidth, borderWidth);
			ctx.lineTo(brickSize - borderWidth, brickSize - borderWidth);
			ctx.lineTo(brickSize, brickSize);
			ctx.lineTo(brickSize, 0);
			ctx.fill();
		}
	}

	// Notch - See legra project for some approaches
	// We used to only do shadows if notch >= 2
	// if (halfNotchSize >= 1) {
	// Drop shadow
	const shadowSize = brickSize * notchShadowSizeRatio;
	// if (shadowSize >= 2 || forceFullDetail) {
	ctx.save();

	const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, shadowSize / 2);
	gradient.addColorStop(0, "rgba(0, 0, 0, 0.86)");
	gradient.addColorStop(0.55, "rgba(0, 0, 0, 0.7)");
	gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
	ctx.fillStyle = gradient;

	ctx.beginPath();
	ctx.translate(
		brickSize * notchShadowPositionRatio.x,
		brickSize * notchShadowPositionRatio.y,
	);
	ctx.rotate((-45 * Math.PI) / 180);
	ctx.scale(1, 0.85);
	ctx.arc(0, 0, shadowSize / 2, 0, 2 * Math.PI, false);
	ctx.fill();
	ctx.restore();
	// context.strokeStyle = "rgba(0, 0, 0, 0.01)";
	// }

	// Ridge of notch
	// if (drawNotchRidgeWidth >= 1 || forceFullDetail) {
	draw45DegreesLinearGradientCircle(
		ctx,
		halfBrickSize,
		halfBrickSize,
		halfNotchSize,
		notchRidgeColour,
		notchRidgeMidColour,
		notchRidgeEndColour,
	);
	// }

	// Notch
	draw45DegreesLinearGradientCircle(
		ctx,
		halfBrickSize,
		halfBrickSize,
		halfNotchSize - drawNotchRidgeWidth,
		colourString,
		notchMidRgbColour,
		notchEndRgbColour,
	);

	// Highlight
	// Couldn't get it to look good when small
	// const highlightSize = brickSize * highlightSizeRatio;
	// if (highlightSize >= 1) {
	// 	ctx.save();

	// 	const gradient = ctx.createRadialGradient(
	// 		0,
	// 		0,
	// 		0,
	// 		0,
	// 		0,
	// 		highlightSize / 2,
	// 	);
	// 	gradient.addColorStop(0, "rgba(255, 255, 255, 0.4)");
	// 	gradient.addColorStop(0.15, "rgba(255, 255, 255, 0.27)");
	// 	gradient.addColorStop(0.45, "rgba(255, 255, 255, 0.08)");
	// 	gradient.addColorStop(0.8, "rgba(255, 255, 255, 0)");
	// 	ctx.fillStyle = gradient;

	// 	ctx.beginPath();
	// 	ctx.translate(
	// 		brickSize * notchHighlightPositionRatio.x,
	// 		brickSize * notchHighlightPositionRatio.y,
	// 	);
	// 	ctx.rotate((45 * Math.PI) / 180);
	// 	ctx.scale(1, 0.85);
	// 	ctx.arc(0, 0, highlightSize / 2, 0, 2 * Math.PI, false);
	// 	ctx.fill();
	// 	ctx.restore();
	// }
	// }

	const ceilBrickSize = Math.ceil(brickSize);
	const imageData = ctx.getImageData(0, 0, ceilBrickSize, ceilBrickSize);
	return imageDataToPixelChannelRows(imageData, brickSize);
}

function renderBrickToPixelChannelRows(
	utilityCtx: CanvasContext,
	colourString: string,
	brickSize: number,
): readonly Uint8ClampedArray[] {
	return baseRenderBrickUnit(utilityCtx, colourString, brickSize, {
		top: true,
		bottom: true,
		left: true,
		right: true,
	});
}

function renderBrickUnitToPixelChannelRows(
	utilityCtx: CanvasContext,
	colourString: string,
	brickSize: number,
): readonly Uint8ClampedArray[] {
	return baseRenderBrickUnit(utilityCtx, colourString, brickSize, {
		top: false,
		bottom: false,
		left: false,
		right: false,
	});
}

function renderBaseplate(
	utilityCtx: CanvasContext,
	baseplateSize: number,
	colourString: string,
	brickSize: number,
) {
	const sizePixelSize = baseplateSize * brickSize;
	const cache: Map<DrawOutlineConfig, readonly Uint8ClampedArray[]> = new Map();
	const output: Uint8ClampedArray[] = Array.from(
		{ length: sizePixelSize },
		() => new Uint8ClampedArray(),
	);
	for (let y = 0; y < baseplateSize; y++) {
		const top = y === 0;
		const bottom = y === baseplateSize - 1;
		const baseplatePixelY = y * brickSize;
		for (let x = 0; x < baseplateSize; x++) {
			const outlines = {
				top,
				bottom,
				left: x === 0,
				right: x === baseplateSize - 1,
			};

			let brickLines = cache.get(outlines);
			if (brickLines === undefined) {
				brickLines = baseRenderBrickUnit(
					utilityCtx,
					colourString,
					brickSize,
					outlines,
				);
				cache.set(outlines, brickLines);
			}

			for (let i = 0; i < brickLines.length; i++) {
				const outputY = baseplatePixelY + i;
				const previous = output[outputY];
				const brickLine = brickLines[i];
				const newLine = new Uint8ClampedArray(
					previous.length + brickLine.length,
				);
				newLine.set(previous, 0);
				newLine.set(brickLine, previous.length);
				output[outputY] = newLine;
			}
		}
	}

	const imageData = new Uint8ClampedArray(sizePixelSize * sizePixelSize * 4);
	for (let lineY = 0; lineY < output.length; lineY++) {
		const imageLine = output[lineY];
		const destLineOffset = lineY * sizePixelSize * 4;
		imageData.set(imageLine, destLineOffset);
	}

	return createImageData(imageData, sizePixelSize, sizePixelSize);
}

export {
	renderBrickToPixelChannelRows,
	renderBrickUnitToPixelChannelRows,
	renderBaseplate,
};
